<template>
  <div class="event-shell">
    <section
      class="event-list"
      :class="{ 'event-list--loading': loadingEvents }"
      :aria-busy="loadingEvents ? 'true' : 'false'"
    >
      <article
        v-for="item in events"
        :key="item.event_id"
        class="event-card card"
      >
        <div class="event-card__main">
          <img
            class="event-card__poster"
            :src="item.image_url"
            :alt="item.event_name"
          >

          <div class="event-card__content">
            <span
              class="status-badge"
              :class="getEventStatus(item.status).className"
            >
              <span aria-hidden="true">●</span>
              {{ getEventStatus(item.status).label }}
            </span>

            <h1>{{ item.event_name }}</h1>
            <p class="muted">{{ item.description }}</p>

            <div class="pill-row">
              <span class="pill">
                <span aria-hidden="true" style="margin-top: 3px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                      <path d="M12.597 21.8a.995.995 0 0 1-1.194 0C6.253 17.976.785 10.109 6.31 4.425A8 8 0 0 1 12 2c2.134 0 4.18.872 5.689 2.424c5.526 5.684.059 13.55-5.092 17.377" />
                      <path d="M12 12a2 2 0 1 0 0-4a2 2 0 0 0 0 4" />
                    </g>
                  </svg>
                </span>
                {{ item.kecamatan }}, {{ item.kabupaten }}
              </span>
            </div>

            <button
              type="button"
              class="btn-primary"
              @click="openEventDetail(item)"
            >
              <span aria-hidden="true" style="margin-top: 2px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 1024 1024">
                  <path d="M0 0h1024v1024H0z" fill="none" />
                  <path fill="currentColor" d="M754.8 480H160a32 32 0 1 0 0 64h594.8L521.3 777.3a32 32 0 0 0 45.4 45.4l288-288a32 32 0 0 0 0-45.4l-288-288a32 32 0 1 0-45.4 45.4z" />
                </svg>
              </span>
              Lihat Detail &amp; Daftar
            </button>
          </div>
        </div>

        <div class="event-card__summary">
          <div class="summary-item">
            <strong :class="getEventStatus(item.status).textClass">
              {{ getEventStatus(item.status).summary }}
            </strong>
            <span>Status event</span>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <strong>{{ formatDate(item.redemption_start) }}</strong>
            <span>Tanggal acara</span>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <strong>{{ formatDate(item.winner_announcement_date) }}</strong>
            <span>Pengumuman pemenang</span>
          </div>
        </div>
      </article>

      <div v-if="!loadingEvents && events.length === 0" class="empty-state card">
        Belum ada event yang tersedia.
      </div>
    </section>

    <nav
      v-if="totalPages > 1"
      class="pagination"
      aria-label="Pagination event"
    >
      <button
        type="button"
        class="pagination__button pagination__button--nav"
        :disabled="currentPage <= 1 || loadingEvents"
        aria-label="Halaman sebelumnya"
        @click="changePage(currentPage - 1)"
      >
        &lsaquo;
        <span>Sebelumnya</span>
      </button>

      <template v-for="(page, index) in paginationPages">
        <span
          v-if="page === 'ellipsis'"
          :key="`ellipsis-${index}`"
          class="pagination__ellipsis"
          aria-hidden="true"
        >
          &hellip;
        </span>

        <button
          v-else
          :key="page"
          type="button"
          class="pagination__button"
          :class="{ 'pagination__button--active': page === currentPage }"
          :disabled="loadingEvents"
          :aria-current="page === currentPage ? 'page' : null"
          :aria-label="`Halaman ${page}`"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
      </template>

      <button
        type="button"
        class="pagination__button pagination__button--nav"
        :disabled="currentPage >= totalPages || loadingEvents"
        aria-label="Halaman berikutnya"
        @click="changePage(currentPage + 1)"
      >
        <span>Berikutnya</span>
        &rsaquo;
      </button>
    </nav>

    <p v-if="metadata.total_data" class="pagination__summary">
      Menampilkan {{ firstItemNumber }}&ndash;{{ lastItemNumber }}
      dari {{ metadata.total_data }} event
    </p>
  </div>
</template>

<script>
import axios from 'axios'
import dayjs from 'dayjs'

const EVENT_STATUSES = {
  REGISTRATION_OPEN: {
    label: 'Pendaftaran Dibuka',
    summary: 'Terbuka',
    className: 'status-badge--open',
    textClass: 'status-text--open'
  },
  REGISTRATION_CLOSED: {
    label: 'Pendaftaran Ditutup',
    summary: 'Ditutup',
    className: 'status-badge--closed',
    textClass: 'status-text--closed'
  },
  ANNOUNCED: {
    label: 'Pemenang Diumumkan',
    summary: 'Diumumkan',
    className: 'status-badge--announced',
    textClass: 'status-text--announced'
  },
  REDEMPTION_OPEN: {
    label: 'Penukaran Dibuka',
    summary: 'Penukaran Dibuka',
    className: 'status-badge--redemption',
    textClass: 'status-text--redemption'
  },
  ENDED: {
    label: 'Event Selesai',
    summary: 'Selesai',
    className: 'status-badge--ended',
    textClass: 'status-text--ended'
  }
}

export default {
  name: 'EventListPage',

  data () {
    return {
      events: [],
      metadata: {},
      currentPage: Math.max(1, Number(this.$route.query.page) || 1),
      pageLimit: Math.max(1, Number(this.$route.query.limit) || 10),
      loadingEvents: false,
      requestId: 0
    }
  },

  computed: {
    totalPages () {
      return Math.max(1, Number(this.metadata.total_page) || 1)
    },
    firstItemNumber () {
      return ((this.currentPage - 1) * this.pageLimit) + 1
    },
    lastItemNumber () {
      return Math.min(
        this.currentPage * this.pageLimit,
        Number(this.metadata.total_data) || 0
      )
    },
    paginationPages () {
      const total = this.totalPages
      const current = this.currentPage

      if (total <= 7) {
        return Array.from({ length: total }, (_, index) => index + 1)
      }

      const pages = [1]
      const start = Math.max(2, current - 1)
      const end = Math.min(total - 1, current + 1)

      if (start > 2) pages.push('ellipsis')
      for (let page = start; page <= end; page += 1) pages.push(page)
      if (end < total - 1) pages.push('ellipsis')

      pages.push(total)
      return pages
    }
  },

  watch: {
    '$route.query.page' (value) {
      const page = Math.max(1, Number(value) || 1)
      if (page !== this.currentPage) this.getEventList(page)
    },
    '$route.query.limit' (value) {
      const limit = Math.max(1, Number(value) || 10)
      if (limit !== this.pageLimit) {
        this.pageLimit = limit
        this.getEventList(1)
      }
    }
  },

  mounted () {
    this.getEventList()
  },

  methods: {
    formatDate (date) {
      return date ? dayjs(date).format('DD MMM YYYY') : '-'
    },
    getEventStatus (status) {
      return EVENT_STATUSES[status] || {
        label: String(status || 'Status Tidak Tersedia')
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, letter => letter.toUpperCase()),
        summary: 'Tidak Tersedia',
        className: 'status-badge--default',
        textClass: 'status-text--default'
      }
    },
    getInitialDetailView (status) {
      return status === 'REGISTRATION_OPEN'
        ? 'detail-open'
        : 'detail-closed'
    },
    async getEventList (page = this.currentPage) {
      const activeRequestId = ++this.requestId
      this.loadingEvents = true

      try {
        const response = await axios.get('member/event-vouchers/member', {
          params: {
            page,
            limit: this.pageLimit
          }
        })

        if (activeRequestId !== this.requestId) return

        this.events = response?.data?.data ?? []
        this.metadata = response?.data?.metadata ?? {}
        this.currentPage = Number(this.metadata.page) || page
        this.pageLimit = Number(this.metadata.limit) || this.pageLimit
      } finally {
        if (activeRequestId === this.requestId) this.loadingEvents = false
      }
    },
    async changePage (page) {
      const targetPage = Math.min(
        Math.max(1, Number(page) || 1),
        this.totalPages
      )

      if (targetPage === this.currentPage || this.loadingEvents) return

      await this.$router.push({
        name: 'event-voucher-list',
        query: {
          ...this.$route.query,
          page: targetPage,
          limit: this.pageLimit
        }
      })

      this.$nextTick(() => {
        this.$el.querySelector('.event-list')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      })
    },
    openEventDetail (item) {
      this.$router.push({
        name: 'event-detail',
        params: {
          id: item.event_id
        },
        query: {
          view: this.getInitialDetailView(item.status)
        }
      })
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.event-shell {
  min-height: calc(100vh - 60px);
  color: #172852;
}

.card {
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(20, 37, 80, .04);
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: opacity .2s;
}

.event-list--loading {
  opacity: .6;
  pointer-events: none;
}

.event-card {
  overflow: hidden;
}

.event-card__main {
  display: grid;
  grid-template-columns: minmax(280px, 39%) minmax(0, 1fr);
  gap: 28px;
  padding: 20px;
}

.event-card__poster {
  width: 100%;
  min-width: 0;
  border-radius: 8px;
  object-fit: contain;
  max-height: 15rem;
}

.event-card__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
}

.event-card__content h1 {
  margin: 14px 0 8px;
  color: #172852;
  font-size: 29px;
  line-height: 1.2;
}

.muted {
  color: #6f7787;
  line-height: 1.55;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}

.status-badge--open { background: #e7f8ef; color: #16814a; }
.status-badge--closed { background: #fff0e8; color: #b65320; }
.status-badge--announced { background: #eef1ff; color: #485bbb; }
.status-badge--redemption { background: #fff5d9; color: #9b6a0b; }
.status-badge--ended,
.status-badge--default { background: #eef1f5; color: #687386; }

.status-text--open { color: #16814a !important; }
.status-text--closed { color: #b65320 !important; }
.status-text--announced { color: #485bbb !important; }
.status-text--redemption { color: #9b6a0b !important; }
.status-text--ended,
.status-text--default { color: #687386 !important; }

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 14px 0 18px;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: 999px;
  background: #eef2f7;
  color: #4d5a72;
  font-size: 12px;
  font-weight: 700;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 9px;
  background: #192e62;
  color: #fff;
  cursor: pointer;
  font-weight: 800;
}

.event-card__summary {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: stretch;
  margin: 0 20px;
  padding: 18px 0;
  border-top: 1px solid #e7ebf1;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 0 22px;
}

.summary-item:first-child { padding-left: 0; }
.summary-item:last-child { padding-right: 0; }
.summary-item strong { color: #172852; font-size: 19px; line-height: 1.2; }
.summary-item span { color: #747d8e; font-size: 12px; }
.summary-divider { width: 1px; background: #e7ebf1; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.pagination__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dce3ee;
  border-radius: 9px;
  background: #fff;
  color: #536078;
  cursor: pointer;
  font-weight: 700;
}

.pagination__button:hover:not(:disabled) { border-color: #192e62; color: #192e62; }
.pagination__button--active { border-color: #192e62; background: #192e62; color: #fff; }
.pagination__button--nav { gap: 7px; }
.pagination__button:disabled { cursor: not-allowed; opacity: .45; }
.pagination__ellipsis { padding: 0 3px; color: #7b8494; }
.pagination__summary { margin: 10px 0 0; color: #747d8e; font-size: 12px; text-align: center; }
.empty-state { padding: 30px; color: #747d8e; text-align: center; }

@media (max-width: 900px) {
  .event-card__main { grid-template-columns: 1fr; }
}

@media (max-width: 640px) {
  .event-shell { padding: 12px; }
  .event-list { gap: 12px; }
  .event-card__main { gap: 14px; padding: 14px; }
  .event-card__content h1 { font-size: 21px; }
  .event-card__summary { grid-template-columns: 1fr; margin: 0 14px; padding: 4px 0; }
  .summary-item { padding: 13px 0; }
  .summary-divider { width: 100%; height: 1px; }
  .pagination { gap: 5px; margin-top: 18px; }
  .pagination__button { min-width: 36px; height: 36px; padding: 0 9px; }
  .pagination__button--nav span { display: none; }
}
</style>
